import type { Plan } from '../../api/payments';

export type DurationUnit = Plan['duration_unit'];

/**
 * Дни доступа — как считает приложение (mobile/src/services/PurchaseService.ts):
 * неделя = 7, месяц = 30, год = 365. По ним же «цена в день».
 */
const DAYS_PER_UNIT: Record<DurationUnit, number> = { week: 7, month: 30, year: 365 };

export function durationDays(value: number, unit: DurationUnit): number {
  return Math.max(0, value) * (DAYS_PER_UNIT[unit] ?? 1);
}

/** Группы разрядов через пробел: 119900 → «119 900». */
export function groupDigits(n: number): string {
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

/** Цифры из строки поля «119 900» → 119900; пусто → null. */
export function parseDigits(value: string): number | null {
  const digits = value.replace(/\D/g, '');
  return digits ? Number(digits) : null;
}

/** Цена в день, округление вниз (как в приложении). null — если дней нет. */
export function perDay(price: number, days: number): number | null {
  if (!price || days <= 0) return null;
  return Math.floor(price / days);
}

function plural(n: number, one: string, few: string, many: string): string {
  const d10 = n % 10;
  const d100 = n % 100;
  if (d10 === 1 && d100 !== 11) return one;
  if (d10 >= 2 && d10 <= 4 && (d100 < 12 || d100 > 14)) return few;
  return many;
}

const UNIT_RU: Record<DurationUnit, [string, string, string]> = {
  week: ['неделя', 'недели', 'недель'],
  month: ['месяц', 'месяца', 'месяцев'],
  year: ['год', 'года', 'лет'],
};

export function daysLabel(days: number): string {
  return `${groupDigits(days)} ${plural(days, 'день', 'дня', 'дней')}`;
}

/** «1 месяц · 30 дней». */
export function durationLabel(value: number, unit: DurationUnit): string {
  const forms = UNIT_RU[unit];
  const unitText = forms ? plural(value, ...forms) : unit;
  return `${value} ${unitText} · ${daysLabel(durationDays(value, unit))}`;
}

/** Подпись плана на экране оплаты (узб.): «1 hafta», «3 oy», «1 yil». */
const UNIT_UZ: Record<DurationUnit, string> = { week: 'hafta', month: 'oy', year: 'yil' };
export function uzDurationTitle(value: number, unit: DurationUnit): string {
  return `${value} ${UNIT_UZ[unit] ?? unit}`;
}

/** Скидка старой цены к цене, %; null — если старая цена не больше цены. */
export function discountPercent(price: number, oldPrice: number | null | undefined): number | null {
  if (!oldPrice || oldPrice <= price || price <= 0) return null;
  const pct = Math.round((1 - price / oldPrice) * 100);
  return pct > 0 ? pct : null;
}

/** Деньги: «49 900 сум» для UZS, иначе Intl. */
export function formatMoney(value: number, currency: string = 'UZS'): string {
  if (!currency || currency.toUpperCase() === 'UZS') return `${groupDigits(value)} сум`;
  try {
    return new Intl.NumberFormat('ru-RU', { style: 'currency', currency, maximumFractionDigits: 0 }).format(value);
  } catch {
    return `${groupDigits(value)} ${currency}`;
  }
}

/** Порядок показа: по длительности, как на экране оплаты. */
export function byDuration(a: Pick<Plan, 'duration_value' | 'duration_unit'>, b: Pick<Plan, 'duration_value' | 'duration_unit'>): number {
  return durationDays(a.duration_value, a.duration_unit) - durationDays(b.duration_value, b.duration_unit);
}
